import { Link } from 'react-router-dom';
import { Mail, Phone, AlertCircle, Calendar, Star, Zap, Briefcase } from 'lucide-react';
import { SourceIndicator } from './SourceIndicator';
import type { RolledUpPerson } from '../../lib/peopleRollup';
import { formatPhone } from '../../lib/formatPhone';

interface PersonContactRowProps {
  rolled: RolledUpPerson;
  onScheduleActivity?: (personId: string, personName: string) => void;
  onConvert?: (personId: string, teamId: string, sportId: string) => void;
  canEdit?: boolean;
}

const effectiveFirstName = (p: RolledUpPerson['person']): string =>
  p.crmFirstName?.trim() || p.firstName;
const effectiveLastName = (p: RolledUpPerson['person']): string =>
  p.crmLastName?.trim() || p.lastName;
const effectiveEmail = (p: RolledUpPerson['person']): string | null =>
  p.crmEmail?.trim() || p.email;
const effectivePhone = (p: RolledUpPerson['person']): string | null =>
  p.crmPhone?.trim() || p.phone;

const formatTeamLevels = (levels: string[]): string =>
  levels.length === 1 ? levels[0] : `(${levels.join(', ')})`;

const formatItem = (
  scope: 'ORG' | 'SPORT',
  item: { role: string | null; teamLevels?: string[] },
): string => {
  if (scope === 'ORG') {
    return `${item.role ?? 'Contact'} · org-level`;
  }
  if (!item.teamLevels) {
    return `${item.role ?? 'Staff'} · sport-level`;
  }
  return `${item.role ?? 'Coach'} ${formatTeamLevels(item.teamLevels)}`;
};

export const PersonContactRow = ({
  rolled,
  onScheduleActivity,
  onConvert,
  canEdit = false,
}: PersonContactRowProps) => {
  const { person } = rolled;
  const firstName = effectiveFirstName(person);
  const lastName = effectiveLastName(person);
  const fullName = `${firstName} ${lastName}`;
  const email = effectiveEmail(person);
  const phone = effectivePhone(person);
  const showConvert =
    !person.blastUserId && !person.convertedAt && rolled.firstTeamLink !== undefined;

  return (
    <div className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
      <div className="flex items-start gap-2">
        {/* Avatar */}
        <div className="flex-shrink-0 h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {firstName[0]}{lastName[0]}
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Name row */}
          <div className="flex items-center gap-2 flex-wrap">
            <SourceIndicator source={person.blastUserId ? 'BLAST' : 'CRM'} />
            <Link
              to={`/people/${person.id}`}
              className="text-sm font-medium text-blast-600 dark:text-blast-400 hover:underline truncate"
            >
              {fullName}
            </Link>
            {rolled.isPrimary && (
              <Star className="h-3 w-3 text-amber-500 fill-current flex-shrink-0" />
            )}
            {rolled.isFormer && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 flex-shrink-0">
                Former{rolled.lastSeasonYear ? ` · last ${rolled.lastSeasonYear}` : ''}
              </span>
            )}
          </div>

          {/* Roles summary */}
          {rolled.displayLinks.length > 0 && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-start gap-1 flex-wrap">
              <Briefcase className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-400" />
              <span>
                {rolled.displayLinks.map((link, idx) => (
                  <span
                    key={`${link.scope}-${link.scope === 'SPORT' ? link.sportId : 'org'}-${idx}`}
                  >
                    {idx > 0 && <span className="text-gray-400 mx-1">·</span>}
                    {link.scope === 'SPORT' && (
                      <strong className="font-medium text-gray-700 dark:text-gray-300">
                        {link.sportName}:{' '}
                      </strong>
                    )}
                    {link.items.map((item, i) => (
                      <span key={i}>
                        {i > 0 && ', '}
                        {formatItem(link.scope, item)}
                      </span>
                    ))}
                  </span>
                ))}
              </span>
            </div>
          )}

          {/* Contact info — stacks on mobile, inline on sm+ */}
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3 mt-1 text-xs">
            {email ? (
              <span className="text-gray-500 dark:text-gray-400 flex items-center min-w-0">
                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="sm:truncate">{email}</span>
              </span>
            ) : (
              <span className="text-red-400 dark:text-red-500 flex items-center">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                No email
              </span>
            )}
            {phone ? (
              <span className="text-gray-500 dark:text-gray-400 flex items-center flex-shrink-0">
                <Phone className="h-3 w-3 mr-1" />
                {formatPhone(phone)}
              </span>
            ) : (
              <span className="text-red-400 dark:text-red-500 flex items-center flex-shrink-0">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                No phone
              </span>
            )}
          </div>

          {/* Convert pill */}
          {showConvert && canEdit && onConvert && rolled.firstTeamLink && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => onConvert(person.id, rolled.firstTeamLink!.teamId, rolled.firstTeamLink!.sportId)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
              >
                <Zap className="h-3 w-3" />
                Convert
              </button>
            </div>
          )}
        </div>

        {/* Right-side action: Schedule */}
        {canEdit && onScheduleActivity && (
          <button
            type="button"
            onClick={() => onScheduleActivity(person.id, fullName)}
            className="p-1 text-gray-400 hover:text-blast-600 dark:hover:text-blast-400 transition-colors flex-shrink-0"
            title="Schedule activity"
          >
            <Calendar className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
